// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Profile / Edit profile  (route /profile/edit — the "Edit profile" button on the profile page)
// One page for everything a customer can change about themselves: photo (take a selfie, choose from the gallery, or remove;
// shrunk on the device by utils/avatarImage before it is sent), full name (required), nickname, and an optional contact email
// (prefills checkout and is where the order confirmation goes). Read-only, with the reason: the phone number (it is the SMS
// login, so it can only change by logging in with another number) and the customer's own referral code (copy/share), plus the
// code they were referred with, if any. Save is enabled only when something changed; inline validation mirrors the server's
// rules (backend/app/api/profile.py); on success it returns to /profile with a confirmation.
// PUT /api/me/profile (utils/profile.ts). Signed-out visitors go to /welcome.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Copy, Images, Lock, User } from "lucide-react";
import PageTransition from "../components/PageTransition";
import Breadcrumbs from "../components/Breadcrumbs";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { AvatarError, fileToAvatarDataUrl } from "../utils/avatarImage";
import { ApiError } from "../utils/api";
import { NAME_MAX, NAME_MIN, fetchProfile, saveProfile, type Profile } from "../utils/profile";

const NICKNAME_MAX = 40;
const EMAIL_MAX = 254;
// Same idea as the server: no markup or control characters in names.
// eslint-disable-next-line no-control-regex
const BAD_TEXT = /[\u0000-\u001f\u007f<>]/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const INPUT =
  "mt-1 w-full rounded-xl border bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:bg-white/5 dark:text-foam-50";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "Z";

type Load = { status: "loading" } | { status: "error" } | { status: "ready"; profile: Profile };

export default function EditProfile() {
  useDocumentTitle("Edit profile");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });

  const refresh = () =>
    fetchProfile().then(
      (profile) => setLoad({ status: "ready", profile }),
      () => setLoad({ status: "error" }),
    );

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Edit profile</h1>
          </header>

          {load.status === "loading" && <p role="status" className="rounded-2xl bg-white p-6 text-center text-sm shadow-sm dark:bg-ocean-900">Loading your profile…</p>}
          {load.status === "error" && (
            <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-lg font-bold">We couldn&apos;t load your profile</p>
              <button type="button" onClick={() => void refresh()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">Try again</button>
            </div>
          )}
          {load.status === "ready" && <EditForm profile={load.profile} phone={load.profile.phone ?? auth.user?.phone ?? ""} />}
        </div>
      </div>
    </PageTransition>
  );
}

function EditForm({ profile, phone }: { profile: Profile; phone: string }) {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<string | null>(profile.photoDataUrl);
  const [name, setName] = useState(profile.fullName ?? "");
  const [nickname, setNickname] = useState(profile.nickname ?? "");
  const [email, setEmail] = useState(profile.email ?? "");
  const [touched, setTouched] = useState({ name: false, nickname: false, email: false });
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const selfie = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);

  const trimmed = { name: name.trim(), nickname: nickname.trim(), email: email.trim() };
  const errors = {
    name:
      trimmed.name.length < NAME_MIN || trimmed.name.length > NAME_MAX
        ? `Enter your name (${NAME_MIN}–${NAME_MAX} characters).`
        : BAD_TEXT.test(trimmed.name)
          ? "Remove special characters from your name."
          : null,
    nickname: trimmed.nickname.length > NICKNAME_MAX ? `Keep it to ${NICKNAME_MAX} characters.` : BAD_TEXT.test(trimmed.nickname) ? "Remove special characters." : null,
    email: trimmed.email !== "" && (!EMAIL.test(trimmed.email) || trimmed.email.length > EMAIL_MAX) ? "Enter a valid email address, like name@example.com." : null,
  };
  const dirty =
    trimmed.name !== (profile.fullName ?? "") || trimmed.nickname !== (profile.nickname ?? "") || trimmed.email !== (profile.email ?? "") || photo !== profile.photoDataUrl;

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoError(null);
    try {
      setPhoto(await fileToAvatarDataUrl(file));
    } catch (caught) {
      setPhotoError(caught instanceof AvatarError ? caught.message : "We couldn't use that photo.");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(profile.referralCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refused: the code is on screen.
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, nickname: true, email: true });
    if (errors.name || errors.nickname || errors.email) {
      (document.querySelector("[aria-invalid=true]") as HTMLElement | null)?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveProfile({ fullName: trimmed.name, nickname: trimmed.nickname || undefined, photoDataUrl: photo, email: trimmed.email || null });
      navigate("/profile", { replace: true, state: { saved: true } });
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 422 ? "One of those details isn't valid. Please check them and try again." : "We couldn't save your profile. Check your connection and try again.");
      setBusy(false);
    }
  };

  const field = (touchedField: boolean, message: string | null) => (touchedField && message ? "border-coral-500" : "border-ocean-900/10 dark:border-white/10");
  const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <section aria-label="Profile photo" className={`${card} flex flex-col items-center`}>
        <div className="relative">
          <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-ocean-800 text-white">
            {photo ? (
              <img src={photo} alt="Your profile photo" className="h-full w-full object-cover" />
            ) : trimmed.name ? (
              <span aria-hidden="true" className="font-display text-3xl font-bold">{initials(trimmed.name)}</span>
            ) : (
              <User className="h-10 w-10" aria-hidden="true" />
            )}
          </div>
          <button type="button" aria-label="Choose a photo" onClick={() => gallery.current?.click()} className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-teal-700 text-white shadow-md hover:bg-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
            <Camera className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <input ref={selfie} type="file" accept="image/*" capture="user" hidden onChange={(e) => { void pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={gallery} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={() => selfie.current?.click()} className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-semibold text-teal-700 hover:bg-ocean-700/15 dark:bg-white/10 dark:text-teal-300">
            <Camera className="h-4 w-4" aria-hidden="true" /> Take selfie
          </button>
          <button type="button" onClick={() => gallery.current?.click()} className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-semibold hover:bg-ocean-700/15 dark:bg-white/10">
            <Images className="h-4 w-4" aria-hidden="true" /> Choose from gallery
          </button>
        </div>
        {photo && <button type="button" onClick={() => setPhoto(null)} className="mt-2 min-h-11 text-sm font-semibold underline underline-offset-2">Remove photo</button>}
        <p className="mt-1 max-w-xs text-center text-xs text-ocean-800/80 dark:text-foam-100/70">JPG, PNG or WebP up to 5 MB. It is shrunk on your device and used only for your account.</p>
        <p role="alert" className="mt-1 text-center text-sm text-coral-600 dark:text-coral-300">{photoError}</p>
      </section>

      <section aria-label="Your details" className={`${card} space-y-4`}>
        <div>
          <label htmlFor="ep-name" className="text-sm font-semibold text-ocean-900 dark:text-foam-100">Full name</label>
          <input id="ep-name" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, name: true }))} autoComplete="name" maxLength={NAME_MAX + 20} aria-invalid={touched.name && errors.name ? true : undefined} aria-describedby="ep-name-msg" className={`${INPUT} ${field(touched.name, errors.name)}`} />
          <p id="ep-name-msg" role="alert" className="mt-1 text-xs font-semibold text-coral-500 dark:text-coral-300">{touched.name ? errors.name : null}</p>
        </div>
        <div>
          <label htmlFor="ep-nick" className="text-sm font-semibold text-ocean-900 dark:text-foam-100">Nickname <span className="font-normal text-ocean-800/70 dark:text-foam-100/65">(optional)</span></label>
          <input id="ep-nick" value={nickname} onChange={(e) => setNickname(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, nickname: true }))} maxLength={NICKNAME_MAX + 10} aria-invalid={touched.nickname && errors.nickname ? true : undefined} aria-describedby="ep-nick-msg" className={`${INPUT} ${field(touched.nickname, errors.nickname)}`} />
          <p id="ep-nick-msg" role="alert" className="mt-1 text-xs font-semibold text-coral-500 dark:text-coral-300">{touched.nickname ? errors.nickname : null}</p>
        </div>
        <div>
          <label htmlFor="ep-email" className="text-sm font-semibold text-ocean-900 dark:text-foam-100">Email <span className="font-normal text-ocean-800/70 dark:text-foam-100/65">(optional)</span></label>
          <input id="ep-email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, email: true }))} autoComplete="email" aria-invalid={touched.email && errors.email ? true : undefined} aria-describedby="ep-email-hint ep-email-msg" className={`${INPUT} ${field(touched.email, errors.email)}`} />
          <p id="ep-email-hint" className="mt-1 text-xs text-ocean-800/75 dark:text-foam-100/70">We use it to fill in checkout and send your order confirmation. Clear it to remove it.</p>
          <p id="ep-email-msg" role="alert" className="text-xs font-semibold text-coral-500 dark:text-coral-300">{touched.email ? errors.email : null}</p>
        </div>
      </section>

      <section aria-label="Account details you can't edit here" className={`${card} space-y-4`}>
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-ocean-900 dark:text-foam-100"><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Phone number</p>
          <p className="mt-1 rounded-xl bg-ocean-700/5 px-4 py-2.5 font-semibold text-ocean-950 dark:bg-white/5 dark:text-foam-50">{phone || "—"}</p>
          <p className="mt-1 text-xs text-ocean-800/75 dark:text-foam-100/70">This is your login number, verified by SMS, so it can&apos;t be changed here. To use another number, log out and log in with it.</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-ocean-900 dark:text-foam-100">Your referral code</p>
          <div className="mt-1 flex items-center justify-between gap-2 rounded-xl bg-ocean-700/5 px-4 py-2 dark:bg-white/5">
            <span className="font-mono font-bold tracking-wider text-ocean-950 dark:text-foam-50">{profile.referralCode}</span>
            <button type="button" onClick={() => void copyCode()} className="inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-sm font-bold text-teal-700 hover:bg-teal-100/60 dark:text-teal-300">
              <Copy className="h-4 w-4" aria-hidden="true" /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {profile.referredByCode && <p className="mt-1 text-xs text-ocean-800/75 dark:text-foam-100/70">You joined with the code <span className="font-mono font-semibold">{profile.referredByCode}</span>.</p>}
        </div>
      </section>

      {error && <p role="alert" className="text-center text-sm font-semibold text-coral-500 dark:text-coral-300">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={!dirty || busy} className="min-h-12 flex-1 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:opacity-50">
          {busy ? "Saving…" : "Save changes"}
        </button>
        <Link to="/profile" className="inline-flex min-h-12 items-center rounded-full px-6 text-sm font-bold text-ocean-900 ring-1 ring-ocean-900/15 hover:bg-ocean-700/5 dark:text-foam-100">Cancel</Link>
      </div>
    </form>
  );
}
